/**
 * VideoConference Component - Full HD Video Calling
 * Features: Full HD video, screen sharing, in-call chat, waiting room, fullscreen, PiP
 * Opens in new tab - Preparer can start meetings, others can only join
 * Clean modern design with device selection
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import useWebRTCFirebase from '../hooks/useWebRTCFirebase';

// Clean SVG Icons Component
const Icons = {
  Mic: ({ size = 24, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
      <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
      <line x1="12" y1="19" x2="12" y2="23"/>
      <line x1="8" y1="23" x2="16" y2="23"/>
    </svg>
  ),
  MicOff: ({ size = 24, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="1" y1="1" x2="23" y2="23"/>
      <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"/>
      <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"/>
      <line x1="12" y1="19" x2="12" y2="23"/>
      <line x1="8" y1="23" x2="16" y2="23"/>
    </svg>
  ),
  Video: ({ size = 24, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="23 7 16 12 23 17 23 7"/>
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
    </svg>
  ),
  VideoOff: ({ size = 24, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34l1 1L23 7v10"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  ),
  Monitor: ({ size = 24, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
      <line x1="8" y1="21" x2="16" y2="21"/>
      <line x1="12" y1="17" x2="12" y2="21"/>
    </svg>
  ),
  Phone: ({ size = 24, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
    </svg>
  ),
  PhoneOff: ({ size = 24, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67m-2.67-3.34a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91"/>
      <line x1="23" y1="1" x2="1" y2="23"/>
    </svg>
  ),
  Speaker: ({ size = 24, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
    </svg>
  ),
  Settings: ({ size = 24, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  ),
  ChevronDown: ({ size = 24, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9"/>
    </svg>
  ),
  Users: ({ size = 24, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  MessageCircle: ({ size = 24, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
    </svg>
  ),
  Link: ({ size = 24, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
    </svg>
  ),
  Maximize: ({ size = 24, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 3 21 3 21 9"/>
      <polyline points="9 21 3 21 3 15"/>
      <line x1="21" y1="3" x2="14" y2="10"/>
      <line x1="3" y1="21" x2="10" y2="14"/>
    </svg>
  ),
  Minimize: ({ size = 24, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="4 14 10 14 10 20"/>
      <polyline points="20 10 14 10 14 4"/>
      <line x1="14" y1="10" x2="21" y2="3"/>
      <line x1="3" y1="21" x2="10" y2="14"/>
    </svg>
  ),
  PictureInPicture: ({ size = 24, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2"/>
      <rect x="12" y="10" width="8" height="8" rx="1"/>
    </svg>
  ),
  X: ({ size = 24, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  Check: ({ size = 24, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  Send: ({ size = 24, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13"/>
      <polygon points="22 2 15 22 11 13 2 9 22 2"/>
    </svg>
  ),
  DoorOpen: ({ size = 24, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M13 4h3a2 2 0 0 1 2 2v14"/>
      <path d="M2 20h3"/>
      <path d="M13 20h9"/>
      <path d="M10 12v.01"/>
      <path d="M13 4.562v16.157a1 1 0 0 1-1.242.97L5 20V5.562a2 2 0 0 1 1.515-1.94l4-1A2 2 0 0 1 13 4.561Z"/>
    </svg>
  ),
  User: ({ size = 24, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  Camera: ({ size = 24, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
      <circle cx="12" cy="13" r="4"/>
    </svg>
  ),
  Lock: ({ size = 24, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  ),
};

function VideoConference({ preparer: preparerProp, company: companyProp, onClose }) {
  // Get user info from localStorage if not passed as props (when opened in new tab)
  // eslint-disable-next-line no-unused-vars
  const [userInfo, setUserInfo] = useState(() => {
    if (preparerProp) {
      return {
        id: preparerProp.id,
        name: preparerProp.name,
        isPreparer: true,
        companyId: companyProp?.id,
        companyName: companyProp?.name
      };
    }
    // Try to get from localStorage
    try {
      const stored = localStorage.getItem('videoConferenceUser');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.log('Error reading user info:', e);
    }
    // Default: guest user
    return {
      id: 'guest-' + Date.now(),
      name: '',
      isPreparer: false,
      companyId: null,
      companyName: null
    };
  });
  
  // Get room ID from URL params if present
  const [initialRoomId] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('room') || '';
  });

  const [view, setView] = useState('lobby'); // 'lobby' | 'waiting' | 'call'
  const [roomInput, setRoomInput] = useState(initialRoomId);
  const [guestName, setGuestName] = useState(userInfo.name || '');
  const [showCopied, setShowCopied] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [callDuration, setCallDuration] = useState(0);
  const [showParticipants, setShowParticipants] = useState(false);
  const [pinnedParticipant, setPinnedParticipant] = useState(null);
  const [previewStream, setPreviewStream] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  
  // Device selection state
  const [audioDevices, setAudioDevices] = useState([]);
  const [videoDevices, setVideoDevices] = useState([]);
  const [speakerDevices, setSpeakerDevices] = useState([]);
  const [selectedMic, setSelectedMic] = useState('');
  const [selectedCamera, setSelectedCamera] = useState('');
  const [selectedSpeaker, setSelectedSpeaker] = useState('');
  const [showDeviceSettings, setShowDeviceSettings] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  
  const localVideoRef = useRef(null);
  const previewVideoRef = useRef(null);
  const remoteVideoRefs = useRef(new Map());
  const screenShareRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const callContainerRef = useRef(null);
  const callStartTimeRef = useRef(null);
  const chatEndRef = useRef(null);
  
  // Start camera preview in lobby
  useEffect(() => {
    let mounted = true;
    
    const startPreview = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { 
            width: { ideal: 1280 }, 
            height: { ideal: 720 }, 
            facingMode: 'user'
          },
          audio: true
        });
        
        if (mounted) {
          setPreviewStream(stream);
          if (previewVideoRef.current) {
            previewVideoRef.current.srcObject = stream;
          }
        }
      } catch (err) {
        console.log('Preview error:', err);
        if (mounted) {
          setPreviewError('Camera access needed for video calls');
        }
      }
    };
    
    if (view === 'lobby') {
      startPreview();
    }
    
    return () => {
      mounted = false;
      // Don't stop preview stream here - we need it for the call
    };
  }, [view]);
  
  // Update preview video element
  useEffect(() => {
    if (previewVideoRef.current && previewStream) {
      previewVideoRef.current.srcObject = previewStream;
    }
  }, [previewStream]);

  // Enumerate media devices
  useEffect(() => {
    const enumerateDevices = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter(d => d.kind === 'audioinput');
        const videoInputs = devices.filter(d => d.kind === 'videoinput');
        const audioOutputs = devices.filter(d => d.kind === 'audiooutput');
        
        setAudioDevices(audioInputs);
        setVideoDevices(videoInputs);
        setSpeakerDevices(audioOutputs);
        
        // Set default selected devices
        if (audioInputs.length > 0 && !selectedMic) {
          setSelectedMic(audioInputs[0].deviceId);
        }
        if (videoInputs.length > 0 && !selectedCamera) {
          setSelectedCamera(videoInputs[0].deviceId);
        }
        if (audioOutputs.length > 0 && !selectedSpeaker) {
          setSelectedSpeaker(audioOutputs[0].deviceId);
        }
      } catch (err) {
        console.log('Error enumerating devices:', err);
      }
    };
    
    enumerateDevices();
    navigator.mediaDevices.addEventListener('devicechange', enumerateDevices);
    
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', enumerateDevices);
    };
  }, [selectedMic, selectedCamera, selectedSpeaker]);

  // Change audio/video device
  const changeDevice = useCallback(async (deviceId, type) => {
    try {
      const constraints = {
        video: type === 'video' ? { deviceId: { exact: deviceId } } : false,
        audio: type === 'audio' ? { deviceId: { exact: deviceId } } : false
      };
      
      const newStream = await navigator.mediaDevices.getUserMedia(constraints);
      
      if (previewStream) {
        const oldTrack = type === 'audio' 
          ? previewStream.getAudioTracks()[0] 
          : previewStream.getVideoTracks()[0];
        const newTrack = type === 'audio'
          ? newStream.getAudioTracks()[0]
          : newStream.getVideoTracks()[0];
        
        if (oldTrack) oldTrack.stop();
        if (newTrack) {
          previewStream.removeTrack(oldTrack);
          previewStream.addTrack(newTrack);
        }
      }
      
      if (type === 'audio') setSelectedMic(deviceId);
      if (type === 'video') setSelectedCamera(deviceId);
    } catch (err) {
      console.log('Error changing device:', err);
    }
  }, [previewStream]);

  // Set speaker output
  const changeSpeaker = useCallback(async (deviceId) => {
    setSelectedSpeaker(deviceId);
    // Apply to all video elements
    const videoElements = document.querySelectorAll('video');
    videoElements.forEach(async (video) => {
      if (video.setSinkId) {
        try {
          await video.setSinkId(deviceId);
        } catch (err) {
          console.log('Error setting speaker:', err);
        }
      }
    });
  }, []);

  const {
    roomId,
    localStream,
    screenShareStream,
    remoteStreams,
    participants,
    audioEnabled,
    videoEnabled,
    isScreenSharing,
    connectionState,
    error,
    isHost,
    waitingGuests,
    waitingStatus,
    joinRoom,
    leaveRoom,
    toggleAudio,
    toggleVideo,
    toggleScreenShare,
    admitGuest,
    declineGuest,
    setError
  } = useWebRTCFirebase();

  // Update local video element when stream changes OR view changes
  useEffect(() => {
    console.log('Local stream effect - view:', view, 'localStream:', !!localStream, 'ref:', !!localVideoRef.current);
    if (localVideoRef.current && localStream) {
      console.log('Setting local video stream, tracks:', localStream.getTracks().map(t => t.kind + ':' + t.enabled));
      localVideoRef.current.srcObject = localStream;
      // Force play
      localVideoRef.current.play().catch(e => console.log('Video play error:', e));
    }
  }, [localStream, view]);

  // Also update when connectionState changes (stream might be ready after connection)
  useEffect(() => {
    if (view === 'call' && localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [connectionState, view, localStream]);

  // Update screen share video element
  useEffect(() => {
    if (screenShareRef.current && screenShareStream) {
      screenShareRef.current.srcObject = screenShareStream;
    }
  }, [screenShareStream]);

  // Update remote video elements when streams change
  useEffect(() => {
    remoteStreams.forEach((stream, oderId) => {
      const videoElement = remoteVideoRefs.current.get(oderId);
      if (videoElement && videoElement.srcObject !== stream) {
        videoElement.srcObject = stream;
      }
    });
  }, [remoteStreams]);

  // Audio level monitoring
  useEffect(() => {
    const stream = previewStream || localStream;
    if (!stream) return;
    
    const audioTrack = stream.getAudioTracks()[0];
    if (!audioTrack) return;
    
    try {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      source.connect(analyserRef.current);
      
      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      
      const checkAudioLevel = () => {
        if (analyserRef.current) {
          analyserRef.current.getByteFrequencyData(dataArray);
          const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
          setAudioLevel(Math.min(100, Math.round(average * 1.5)));
        }
      };
      
      const interval = setInterval(checkAudioLevel, 100);
      
      return () => {
        clearInterval(interval);
        if (audioContextRef.current) {
          audioContextRef.current.close();
        }
      };
    } catch (err) {
      console.log('Audio context error:', err);
    }
  }, [previewStream, localStream]);

  // Call duration timer
  useEffect(() => {
    let interval;
    if (view === 'call' && connectionState === 'in-call') {
      if (!callStartTimeRef.current) {
        callStartTimeRef.current = Date.now();
      }
      interval = setInterval(() => {
        setCallDuration(Math.floor((Date.now() - callStartTimeRef.current) / 1000));
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [view, connectionState]);

  // Handle waiting room status
  useEffect(() => {
    if (waitingStatus === 'waiting') {
      setView('waiting');
    } else if (waitingStatus === 'admitted' || connectionState === 'in-call') {
      setView('call');
    } else if (waitingStatus === 'declined') {
      setTimeout(() => {
        setView('lobby');
        setError('The host did not admit you to the meeting');
      }, 2000);
    }
  }, [waitingStatus, connectionState, setError]);

  // Auto scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Fullscreen handling
  const toggleFullscreen = useCallback(async () => {
    if (!document.fullscreenElement) {
      try {
        await callContainerRef.current?.requestFullscreen();
        setIsFullscreen(true);
      } catch (err) {
        console.log('Fullscreen error:', err);
      }
    } else {
      await document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Picture-in-Picture
  const togglePiP = useCallback(async () => {
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else if (localVideoRef.current) {
        await localVideoRef.current.requestPictureInPicture();
      }
    } catch (err) {
      console.log('PiP error:', err);
    }
  }, []);

  // Format call duration
  const formatDuration = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Generate a random room ID
  const generateRoomId = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let result = '';
    for (let i = 0; i < 9; i++) {
      if (i === 3 || i === 6) result += '-';
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  // Handle creating a new meeting
  const handleCreateMeeting = async () => {
    const newRoomId = generateRoomId();
    setRoomInput(newRoomId);
    await handleJoinRoom(newRoomId, true);
  };

  // Handle joining a room
  const handleJoinRoom = async (roomIdToJoin = roomInput, isCreator = false) => {
    if (!roomIdToJoin.trim()) {
      setError('Please enter a room ID');
      return;
    }
    
    // For guests, require a name
    const userName = userInfo.isPreparer ? userInfo.name : guestName;
    if (!userName?.trim()) {
      setError('Please enter your name');
      return;
    }
    
    // Stop preview stream and wait for camera to be released
    if (previewStream) {
      console.log('Stopping preview stream...');
      previewStream.getTracks().forEach(track => {
        track.stop();
        console.log('Stopped track:', track.kind);
      });
      setPreviewStream(null);
      // Wait a bit for camera to be released
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    try {
      console.log('Joining room:', roomIdToJoin);
      await joinRoom(
        roomIdToJoin.trim().toUpperCase(),
        userInfo.id || 'guest-' + Date.now(),
        userName,
        isCreator
      );
      console.log('Join room completed, isHost:', isHost, 'isCreator:', isCreator);
      if (!isHost && !isCreator) {
        // Will be handled by waitingStatus effect
      } else {
        setView('call');
      }
    } catch (err) {
      console.error('Failed to join room:', err);
    }
  };

  // Handle leaving the call
  const handleLeaveCall = () => {
    leaveRoom();
    setView('lobby');
    setRoomInput('');
    callStartTimeRef.current = null;
    setCallDuration(0);
    setChatMessages([]);
  };

  // Copy room link to clipboard
  const copyRoomLink = () => {
    const link = `${window.location.origin}/video-conference?room=${roomId}`;
    navigator.clipboard.writeText(link);
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 2000);
  };

  // Share via WhatsApp
  const shareViaWhatsApp = () => {
    const link = `${window.location.origin}/video-conference?room=${roomId}`;
    const text = `Join my video call: ${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  // Share via SMS
  const shareViaSMS = () => {
    const link = `${window.location.origin}/video-conference?room=${roomId}`;
    const text = `Join my video call: ${link}`;
    window.open(`sms:?body=${encodeURIComponent(text)}`, '_blank');
  };

  // Share via Email
  const shareViaEmail = () => {
    const link = `${window.location.origin}/video-conference?room=${roomId}`;
    const subject = `Video Call Invitation - ${userInfo.companyName || 'Tax Services'}`;
    const body = `You are invited to join a video call.\n\nRoom ID: ${roomId}\nJoin Link: ${link}\n\nClick the link to join the meeting.`;
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
  };

  // Send chat message
  const sendChatMessage = () => {
    if (!chatInput.trim()) return;
    const newMessage = {
      id: Date.now(),
      sender: userInfo.name || 'You',
      text: chatInput.trim(),
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    setChatMessages(prev => [...prev, newMessage]);
    setChatInput('');
  };

  // Waiting Room View
  if (view === 'waiting') {
    return (
      <div style={styles.container}>
        <div style={styles.waitingRoomCard}>
          <div style={styles.waitingAnimation}>
            <div style={styles.pulseCircle}></div>
            <div style={styles.waitingIcon}>🚪</div>
          </div>
          <h2 style={styles.waitingTitle}>Waiting to be admitted</h2>
          <p style={styles.waitingSubtitle}>
            The host will let you in soon...
          </p>
          <div style={styles.waitingInfo}>
            <span>Room: </span>
            <strong>{roomId}</strong>
          </div>
          
          {/* Preview your video while waiting */}
          <div style={styles.waitingPreview}>
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              style={styles.waitingVideo}
            />
            <div style={styles.previewControls}>
              <button
                onClick={toggleAudio}
                style={{
                  ...styles.previewButton,
                  background: audioEnabled ? 'rgba(255,255,255,0.2)' : 'rgba(239,68,68,0.8)'
                }}
              >
                {audioEnabled ? <Icons.Mic size={20} /> : <Icons.MicOff size={20} />}
              </button>
              <button
                onClick={toggleVideo}
                style={{
                  ...styles.previewButton,
                  background: videoEnabled ? 'rgba(255,255,255,0.2)' : 'rgba(239,68,68,0.8)'
                }}
              >
                {videoEnabled ? <Icons.Video size={20} /> : <Icons.VideoOff size={20} />}
              </button>
            </div>
          </div>
          
          <button onClick={handleLeaveCall} style={styles.cancelButton}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // Lobby View
  if (view === 'lobby') {
    const handleClose = () => {
      // Stop preview stream when closing
      if (previewStream) {
        previewStream.getTracks().forEach(track => track.stop());
      }
      if (onClose) {
        onClose();
      } else {
        window.close();
      }
    };
    
    return (
      <div style={styles.container}>
        <div style={styles.lobbyCard}>
          <div style={styles.header}>
            <div style={styles.logoSection}>
              <div style={styles.videoIcon}>📹</div>
              <div>
                <h1 style={styles.title}>Video Conference</h1>
                <p style={styles.hdBadge}>Full HD • Screen Share • Chat</p>
              </div>
            </div>
            <button onClick={handleClose} style={styles.closeButton}>✕</button>
          </div>

          {error && (
            <div style={styles.errorBanner}>
              <span>⚠️</span> {error}
              <button onClick={() => setError(null)} style={styles.dismissError}>×</button>
            </div>
          )}
          
          {previewError && (
            <div style={styles.warningBanner}>
              <span>📷</span> {previewError}
            </div>
          )}

          {/* Video Preview */}
          <div style={styles.videoPreview}>
            <video
              ref={previewVideoRef}
              autoPlay
              playsInline
              muted
              style={styles.previewVideo}
            />
            {!previewStream && !previewError && (
              <div style={styles.previewLoading}>
                <div style={styles.spinner}></div>
                <p>Starting camera...</p>
              </div>
            )}
            <div style={styles.previewLabel}>
              {userInfo.isPreparer ? `👤 ${userInfo.name}` : (guestName || 'Guest')}
            </div>
          </div>

          <div style={styles.lobbyContent}>
            {/* Guest joining via shared link - Show simplified join view */}
            {!userInfo.isPreparer && initialRoomId && (
              <>
                <div style={styles.inviteCard}>
                  <span style={{fontSize: '2.5rem', marginBottom: '12px', display: 'block'}}>🎥</span>
                  <h3 style={{margin: '0 0 8px', color: '#0f172a', fontSize: '1.1rem'}}>You're invited to a video call</h3>
                  <p style={{margin: '0', color: '#64748b', fontSize: '0.9rem'}}>Room: <strong>{initialRoomId}</strong></p>
                </div>
                
                <div style={styles.section}>
                  <input
                    type="text"
                    placeholder="Enter your name to join"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && guestName.trim() && handleJoinRoom()}
                    style={styles.nameInput}
                    autoFocus
                  />
                </div>
                
                <div style={styles.section}>
                  <button 
                    onClick={() => handleJoinRoom()}
                    style={styles.primaryButton}
                    disabled={!guestName.trim() || connectionState === 'connecting'}
                  >
                    {connectionState === 'connecting' ? (
                      <span>Joining...</span>
                    ) : (
                      <>
                        <span style={styles.buttonIcon}>📹</span>
                        Join Video Call
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
            
            {/* Regular guest view (no room ID in URL) */}
            {!userInfo.isPreparer && !initialRoomId && (
              <>
                <div style={styles.section}>
                  <input
                    type="text"
                    placeholder="Enter your name"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    style={styles.nameInput}
                  />
                </div>
                
                <div style={styles.section}>
                  <p style={styles.sectionLabel}>Enter Meeting Room ID</p>
                  <div style={styles.inputGroup}>
                    <input
                      type="text"
                      placeholder="Room ID (e.g., ABC-DEF-GHI)"
                      value={roomInput}
                      onChange={(e) => setRoomInput(e.target.value.toUpperCase())}
                      onKeyPress={(e) => e.key === 'Enter' && handleJoinRoom()}
                      style={styles.input}
                      maxLength={11}
                    />
                    <button 
                      onClick={() => handleJoinRoom()}
                      style={styles.joinButton}
                      disabled={!roomInput.trim() || connectionState === 'connecting' || !guestName.trim()}
                    >
                      Join
                    </button>
                  </div>
                  <p style={styles.buttonHint}>Ask your preparer for the meeting ID</p>
                </div>
              </>
            )}
            
            {/* Preparer view - Can create or join meetings */}
            {userInfo.isPreparer && (
              <>
                <div style={styles.section}>
                  <button 
                    onClick={handleCreateMeeting}
                    style={styles.primaryButton}
                    disabled={connectionState === 'connecting'}
                  >
                    {connectionState === 'connecting' ? (
                      <span>Starting...</span>
                    ) : (
                      <>
                        <span style={styles.buttonIcon}>🎬</span>
                        Start New Meeting
                      </>
                    )}
                  </button>
                  <p style={styles.buttonHint}>Create a new HD video call</p>
                </div>

                <div style={styles.divider}>
                  <span style={styles.dividerText}>OR</span>
                </div>
                
                <div style={styles.section}>
                  <p style={styles.sectionLabel}>Join Existing Meeting</p>
                  <div style={styles.inputGroup}>
                    <input
                      type="text"
                      placeholder="Room ID (e.g., ABC-DEF-GHI)"
                      value={roomInput}
                      onChange={(e) => setRoomInput(e.target.value.toUpperCase())}
                      onKeyPress={(e) => e.key === 'Enter' && handleJoinRoom()}
                      style={styles.input}
                      maxLength={11}
                    />
                    <button 
                      onClick={() => handleJoinRoom()}
                      style={styles.joinButton}
                      disabled={!roomInput.trim() || connectionState === 'connecting'}
                    >
                      Join
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Features List */}
            <div style={styles.featuresList}>
              <div style={styles.featureItem}>
                <span style={styles.featureIcon}><Icons.Video size={18} color="#3b82f6" /></span>
                <span>Full HD Video (1080p)</span>
              </div>
              <div style={styles.featureItem}>
                <span style={styles.featureIcon}><Icons.Monitor size={18} color="#8b5cf6" /></span>
                <span>HD Screen Sharing</span>
              </div>
              <div style={styles.featureItem}>
                <span style={styles.featureIcon}><Icons.MessageCircle size={18} color="#10b981" /></span>
                <span>In-Call Chat</span>
              </div>
              <div style={styles.featureItem}>
                <span style={styles.featureIcon}><Icons.Lock size={18} color="#f59e0b" /></span>
                <span>Secure & Encrypted</span>
              </div>
            </div>
          </div>

          <div style={styles.footer}>
            <p style={styles.footerText}>
              {userInfo.companyName || 'Tax Services'} • Secure Video Conferencing
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Call View
  const totalParticipants = participants.size + 1;
  const hasScreenShare = isScreenSharing || Array.from(participants.values()).some(p => p?.isScreenSharing);

  return (
    <div ref={callContainerRef} style={styles.callContainer}>
      {/* Header */}
      <div style={styles.callHeader}>
        <div style={styles.headerLeft}>
          <div style={styles.roomBadge}>
            <span style={styles.liveDot}></span>
            <span style={styles.roomIdText}>{roomId}</span>
          </div>
          <div style={styles.durationBadge}>
            {formatDuration(callDuration)}
          </div>
          {isHost && <span style={styles.hostBadge}>Host</span>}
        </div>
        
        <div style={styles.headerCenter}>
          <span style={styles.hdIndicator}>HD</span>
          <span style={styles.participantBadge}>
            <Icons.Users size={14} /> {totalParticipants}
          </span>
        </div>
        
        <div style={styles.headerRight}>
          <button onClick={copyRoomLink} style={styles.headerButton} title="Copy Link">
            {showCopied ? <Icons.Check size={18} color="#22c55e" /> : <Icons.Link size={18} />}
          </button>
          <button 
            onClick={() => setShowParticipants(!showParticipants)} 
            style={{...styles.headerButton, ...(showParticipants ? styles.headerBtnActive : {})}}
            title="Participants"
          >
            <Icons.Users size={18} />
          </button>
          <button 
            onClick={() => setShowChat(!showChat)} 
            style={{...styles.headerButton, position: 'relative', ...(showChat ? styles.headerBtnActive : {})}}
            title="Chat"
          >
            <Icons.MessageCircle size={18} />
            {chatMessages.length > 0 && (
              <span style={styles.chatBadge}>{chatMessages.length}</span>
            )}
          </button>
          <button onClick={toggleFullscreen} style={styles.headerButton} title="Fullscreen">
            {isFullscreen ? <Icons.Minimize size={18} /> : <Icons.Maximize size={18} />}
          </button>
        </div>
      </div>

      {/* Host: Waiting Room Notifications */}
      {isHost && waitingGuests.size > 0 && (
        <div style={styles.waitingNotification}>
          <span style={styles.waitingNotificationText}>
            <Icons.DoorOpen size={18} /> {waitingGuests.size} guest(s) waiting to join
          </span>
          <div style={styles.waitingActions}>
            {Array.from(waitingGuests.entries()).map(([guestId, guest]) => (
              <div key={guestId} style={styles.waitingGuest}>
                <span>{guest.userName || 'Guest'}</span>
                <button onClick={() => admitGuest(guestId)} style={styles.admitBtn}>
                  <Icons.Check size={14} /> Admit
                </button>
                <button onClick={() => declineGuest(guestId)} style={styles.declineBtn}>
                  <Icons.X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div style={styles.mainContent}>
        {/* Video Grid */}
        <div style={{
          ...styles.videoGrid,
          ...(showChat || showParticipants ? { marginRight: '320px' } : {})
        }}>
          {/* Screen Share (if any) - takes priority */}
          {hasScreenShare && (
            <div style={styles.screenShareMain}>
              {isScreenSharing ? (
                <video
                  ref={screenShareRef}
                  autoPlay
                  playsInline
                  style={styles.screenShareVideo}
                />
              ) : (
                Array.from(remoteStreams.entries()).map(([oderId, stream]) => {
                  const participant = participants.get(oderId);
                  if (participant?.isScreenSharing) {
                    return (
                      <video
                        key={`screen-${oderId}`}
                        ref={el => {
                          if (el && el.srcObject !== stream) {
                            el.srcObject = stream;
                          }
                        }}
                        autoPlay
                        playsInline
                        style={styles.screenShareVideo}
                      />
                    );
                  }
                  return null;
                })
              )}
              <div style={styles.screenShareLabel}>
                🖥️ {isScreenSharing ? 'You are sharing your screen' : 'Screen being shared'}
              </div>
            </div>
          )}

          {/* Video Tiles */}
          <div style={{
            ...styles.videoTiles,
            ...(hasScreenShare ? styles.videoTilesWithScreen : {})
          }}>
            {/* Local Video */}
            <div 
              style={{
                ...styles.videoTile,
                ...(pinnedParticipant === 'local' ? styles.pinnedTile : {})
              }}
              onClick={() => setPinnedParticipant(pinnedParticipant === 'local' ? null : 'local')}
            >
              <video
                ref={el => {
                  localVideoRef.current = el;
                  if (el && localStream) {
                    if (el.srcObject !== localStream) {
                      console.log('Setting video srcObject in ref callback');
                      el.srcObject = localStream;
                    }
                    // Ensure video plays
                    el.play().catch(() => {});
                  }
                }}
                autoPlay
                playsInline
                muted
                style={{
                  ...styles.tileVideo,
                  transform: 'scaleX(-1)' // Mirror local video
                }}
              />
              {/* Show loading state if no stream yet */}
              {!localStream && (
                <div style={styles.noVideoOverlay}>
                  <span style={{fontSize: '3rem'}}>📷</span>
                  <p style={{color: 'white', margin: '8px 0 0'}}>Starting camera...</p>
                </div>
              )}
              {/* Show placeholder if video is disabled */}
              {localStream && !videoEnabled && (
                <div style={styles.noVideoOverlay}>
                  <span style={{display: 'flex', marginBottom: '8px'}}><Icons.User size={48} color="rgba(255,255,255,0.6)" /></span>
                  <p style={{color: 'white', margin: '0'}}>Camera off</p>
                </div>
              )}
              <div style={styles.tileOverlay}>
                <div style={styles.tileName}>
                  You {isScreenSharing ? '(Sharing)' : ''}
                </div>
                <div style={styles.tileStatus}>
                  {!audioEnabled && <span style={styles.statusIcon}><Icons.MicOff size={14} /></span>}
                  {!videoEnabled && <span style={styles.statusIcon}><Icons.VideoOff size={14} /></span>}
                </div>
              </div>
            </div>

            {/* Remote Videos */}
            {Array.from(remoteStreams.entries()).map(([oderId, stream]) => {
              const participant = participants.get(oderId);
              return (
                <div 
                  key={oderId} 
                  style={{
                    ...styles.videoTile,
                    ...(pinnedParticipant === oderId ? styles.pinnedTile : {})
                  }}
                  onClick={() => setPinnedParticipant(pinnedParticipant === oderId ? null : oderId)}
                >
                  <video
                    ref={el => {
                      if (el) {
                        remoteVideoRefs.current.set(oderId, el);
                        if (el.srcObject !== stream) {
                          el.srcObject = stream;
                        }
                      }
                    }}
                    autoPlay
                    playsInline
                    style={styles.tileVideo}
                  />
                  <div style={styles.tileOverlay}>
                    <div style={styles.tileName}>
                      {participant?.userName || 'Participant'}
                    </div>
                    <div style={styles.tileStatus}>
                      {!participant?.audioEnabled && <span style={styles.statusIcon}><Icons.MicOff size={14} /></span>}
                      {!participant?.videoEnabled && <span style={styles.statusIcon}><Icons.VideoOff size={14} /></span>}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Empty state when alone */}
            {remoteStreams.size === 0 && (
              <div style={styles.emptyTile}>
                <div style={styles.emptyIcon}><Icons.Users size={48} /></div>
                <h3 style={styles.emptyTitle}>Waiting for participants...</h3>
                <p style={styles.emptySubtitle}>Share the meeting link to invite others</p>
                <div style={styles.shareOptions}>
                  <button onClick={shareViaWhatsApp} style={styles.shareBtn}>
                    WhatsApp
                  </button>
                  <button onClick={shareViaSMS} style={styles.shareBtn}>
                    SMS
                  </button>
                  <button onClick={shareViaEmail} style={styles.shareBtn}>
                    Email
                  </button>
                  <button onClick={copyRoomLink} style={{...styles.shareBtn, ...styles.shareBtnPrimary}}>
                    <Icons.Link size={16} /> Copy Link
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Chat Panel */}
        {showChat && (
          <div style={styles.sidePanel}>
            <div style={styles.panelHeader}>
              <h3 style={styles.panelTitle}>
                <Icons.MessageCircle size={18} /> Chat
              </h3>
              <button onClick={() => setShowChat(false)} style={styles.panelClose}>
                <Icons.X size={16} />
              </button>
            </div>
            <div style={styles.chatMessages}>
              {chatMessages.length === 0 ? (
                <div style={styles.chatEmpty}>No messages yet</div>
              ) : (
                chatMessages.map(msg => (
                  <div key={msg.id} style={styles.chatMessage}>
                    <div style={styles.chatMsgHeader}>
                      <span style={styles.chatSender}>{msg.sender}</span>
                      <span style={styles.chatTime}>{msg.time}</span>
                    </div>
                    <div style={styles.chatText}>{msg.text}</div>
                  </div>
                ))
              )}
              <div ref={chatEndRef} />
            </div>
            <div style={styles.chatInputArea}>
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                placeholder="Type a message..."
                style={styles.chatInput}
              />
              <button onClick={sendChatMessage} style={styles.chatSendBtn}>
                <Icons.Send size={18} />
              </button>
            </div>
          </div>
        )}

        {/* Participants Panel */}
        {showParticipants && (
          <div style={styles.sidePanel}>
            <div style={styles.panelHeader}>
              <h3 style={styles.panelTitle}>
                <Icons.Users size={18} /> Participants ({totalParticipants})
              </h3>
              <button onClick={() => setShowParticipants(false)} style={styles.panelClose}>
                <Icons.X size={16} />
              </button>
            </div>
            <div style={styles.participantsList}>
              {/* Self */}
              <div style={styles.participantItem}>
                <div style={styles.participantAvatar}>
                  {(userInfo.name || 'Y')[0].toUpperCase()}
                </div>
                <div style={styles.participantInfo}>
                  <span style={styles.participantName}>{userInfo.name || 'You'} (You)</span>
                  <span style={styles.participantRole}>{isHost ? 'Host' : 'Participant'}</span>
                </div>
                <div style={styles.participantStatus}>
                  {audioEnabled ? <Icons.Mic size={16} color="#22c55e" /> : <Icons.MicOff size={16} color="#ef4444" />}
                  {videoEnabled ? <Icons.Video size={16} color="#22c55e" /> : <Icons.VideoOff size={16} color="#ef4444" />}
                </div>
              </div>
              
              {/* Other Participants */}
              {Array.from(participants.entries()).map(([oderId, p]) => (
                <div key={oderId} style={styles.participantItem}>
                  <div style={styles.participantAvatar}>
                    {(p?.userName || 'P')[0].toUpperCase()}
                  </div>
                  <div style={styles.participantInfo}>
                    <span style={styles.participantName}>{p?.userName || 'Participant'}</span>
                    <span style={styles.participantRole}>Participant</span>
                  </div>
                  <div style={styles.participantStatus}>
                    {p?.audioEnabled ? <Icons.Mic size={16} color="#22c55e" /> : <Icons.MicOff size={16} color="#ef4444" />}
                    {p?.videoEnabled ? <Icons.Video size={16} color="#22c55e" /> : <Icons.VideoOff size={16} color="#ef4444" />}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Control Bar */}
      <div style={styles.controlBar}>
        {/* Left Group - Audio/Video Controls */}
        <div style={styles.controlGroup}>
          <div style={styles.controlBtnWrapper}>
            <button
              onClick={toggleAudio}
              style={{
                ...styles.controlBtn,
                ...(audioEnabled ? {} : styles.controlBtnOff)
              }}
              title={audioEnabled ? 'Mute' : 'Unmute'}
            >
              <span style={styles.controlIcon}>
                {audioEnabled ? <Icons.Mic size={22} /> : <Icons.MicOff size={22} />}
              </span>
              <span style={styles.controlText}>{audioEnabled ? 'Mute' : 'Unmute'}</span>
            </button>
            {/* Audio Level Indicator */}
            {audioEnabled && (
              <div style={styles.audioLevelBar}>
                <div style={{
                  ...styles.audioLevel,
                  width: `${audioLevel}%`,
                  background: audioLevel > 70 ? '#22c55e' : audioLevel > 30 ? '#3b82f6' : '#64748b'
                }} />
              </div>
            )}
          </div>

          <button
            onClick={toggleVideo}
            style={{
              ...styles.controlBtn,
              ...(videoEnabled ? {} : styles.controlBtnOff)
            }}
            title={videoEnabled ? 'Stop Video' : 'Start Video'}
          >
            <span style={styles.controlIcon}>
              {videoEnabled ? <Icons.Video size={22} /> : <Icons.VideoOff size={22} />}
            </span>
            <span style={styles.controlText}>{videoEnabled ? 'Stop' : 'Start'}</span>
          </button>
        </div>

        {/* Center Group - Screen Share, PiP, Chat, Settings */}
        <div style={styles.controlGroup}>
          <button
            onClick={toggleScreenShare}
            style={{
              ...styles.controlBtn,
              ...(isScreenSharing ? styles.controlBtnActive : {})
            }}
            title={isScreenSharing ? 'Stop Sharing' : 'Share Screen'}
          >
            <span style={styles.controlIcon}><Icons.Monitor size={22} /></span>
            <span style={styles.controlText}>{isScreenSharing ? 'Stop' : 'Share'}</span>
          </button>

          <button
            onClick={togglePiP}
            style={styles.controlBtn}
            title="Picture in Picture"
          >
            <span style={styles.controlIcon}><Icons.PictureInPicture size={22} /></span>
            <span style={styles.controlText}>PiP</span>
          </button>

          <button
            onClick={() => setShowChat(!showChat)}
            style={{
              ...styles.controlBtn,
              ...(showChat ? styles.controlBtnActive : {})
            }}
            title="Chat"
          >
            <span style={styles.controlIcon}><Icons.MessageCircle size={22} /></span>
            <span style={styles.controlText}>Chat</span>
            {chatMessages.length > 0 && (
              <span style={styles.controlBadge}>{chatMessages.length}</span>
            )}
          </button>

          {/* Device Settings Button */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowDeviceSettings(!showDeviceSettings)}
              style={{
                ...styles.controlBtn,
                ...(showDeviceSettings ? styles.controlBtnActive : {})
              }}
              title="Device Settings"
            >
              <span style={styles.controlIcon}><Icons.Settings size={22} /></span>
              <span style={styles.controlText}>Devices</span>
            </button>

            {/* Device Settings Dropdown */}
            {showDeviceSettings && (
              <div style={styles.deviceDropdown}>
                <div style={styles.deviceDropdownHeader}>
                  <Icons.Settings size={18} />
                  <span>Device Settings</span>
                  <button onClick={() => setShowDeviceSettings(false)} style={styles.deviceDropdownClose}>
                    <Icons.X size={16} />
                  </button>
                </div>
                
                {/* Microphone Selection */}
                <div style={styles.deviceSection}>
                  <label style={styles.deviceLabel}>
                    <Icons.Mic size={16} />
                    Microphone
                  </label>
                  <select
                    value={selectedMic}
                    onChange={(e) => changeDevice(e.target.value, 'audio')}
                    style={styles.deviceSelect}
                  >
                    {audioDevices.map(device => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label || 'Microphone ' + (audioDevices.indexOf(device) + 1)}
                      </option>
                    ))}
                  </select>
                  {/* Mic Level Indicator */}
                  <div style={styles.micLevelContainer}>
                    <div style={{
                      ...styles.micLevelBar,
                      width: `${audioLevel}%`
                    }} />
                  </div>
                </div>

                {/* Camera Selection */}
                <div style={styles.deviceSection}>
                  <label style={styles.deviceLabel}>
                    <Icons.Camera size={16} />
                    Camera
                  </label>
                  <select
                    value={selectedCamera}
                    onChange={(e) => changeDevice(e.target.value, 'video')}
                    style={styles.deviceSelect}
                  >
                    {videoDevices.map(device => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label || 'Camera ' + (videoDevices.indexOf(device) + 1)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Speaker Selection */}
                <div style={styles.deviceSection}>
                  <label style={styles.deviceLabel}>
                    <Icons.Speaker size={16} />
                    Speaker
                  </label>
                  <select
                    value={selectedSpeaker}
                    onChange={(e) => changeSpeaker(e.target.value)}
                    style={styles.deviceSelect}
                  >
                    {speakerDevices.length > 0 ? (
                      speakerDevices.map(device => (
                        <option key={device.deviceId} value={device.deviceId}>
                          {device.label || 'Speaker ' + (speakerDevices.indexOf(device) + 1)}
                        </option>
                      ))
                    ) : (
                      <option value="">System Default</option>
                    )}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Group - Leave Call */}
        <div style={styles.controlGroup}>
          <button
            onClick={handleLeaveCall}
            style={styles.leaveBtn}
            title="Leave Call"
          >
            <span style={styles.controlIcon}><Icons.PhoneOff size={22} /></span>
            <span style={styles.controlText}>Leave</span>
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px'
  },
  lobbyCard: {
    background: 'white',
    borderRadius: '24px',
    padding: '40px',
    maxWidth: '480px',
    width: '100%',
    boxShadow: '0 25px 60px rgba(0,0,0,0.3)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px'
  },
  logoSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  },
  videoIcon: {
    fontSize: '3rem'
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: '700',
    color: '#0f172a',
    margin: 0
  },
  hdBadge: {
    fontSize: '0.75rem',
    color: '#64748b',
    marginTop: '4px'
  },
  closeButton: {
    background: '#f1f5f9',
    border: 'none',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    fontSize: '1.2rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#64748b',
    transition: 'all 0.2s'
  },
  errorBanner: {
    background: '#fef2f2',
    color: '#dc2626',
    padding: '12px 16px',
    borderRadius: '12px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '0.9rem'
  },
  dismissError: {
    marginLeft: 'auto',
    background: 'none',
    border: 'none',
    fontSize: '1.2rem',
    cursor: 'pointer',
    color: '#dc2626'
  },
  warningBanner: {
    background: '#fef3c7',
    color: '#92400e',
    padding: '12px 16px',
    borderRadius: '12px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '0.9rem'
  },
  videoPreview: {
    position: 'relative',
    borderRadius: '16px',
    overflow: 'hidden',
    marginBottom: '20px',
    background: '#1e293b',
    aspectRatio: '16/9'
  },
  previewVideo: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transform: 'scaleX(-1)'
  },
  previewLoading: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',
    color: 'white'
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid rgba(255,255,255,0.3)',
    borderTopColor: 'white',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 10px'
  },
  previewLabel: {
    position: 'absolute',
    bottom: '12px',
    left: '12px',
    background: 'rgba(0,0,0,0.6)',
    color: 'white',
    padding: '8px 14px',
    borderRadius: '8px',
    fontSize: '0.85rem',
    fontWeight: '500'
  },
  nameInput: {
    width: '100%',
    padding: '16px 18px',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    fontSize: '1rem',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box'
  },
  sectionLabel: {
    fontSize: '0.9rem',
    fontWeight: '600',
    color: '#475569',
    marginBottom: '10px'
  },
  lobbyContent: {
    marginBottom: '24px'
  },
  inviteCard: {
    background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
    border: '2px solid #bae6fd',
    borderRadius: '16px',
    padding: '24px',
    textAlign: 'center',
    marginBottom: '20px'
  },
  section: {
    marginBottom: '16px'
  },
  primaryButton: {
    width: '100%',
    padding: '18px 24px',
    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '14px',
    fontSize: '1.1rem',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    transition: 'transform 0.2s, box-shadow 0.2s',
    boxShadow: '0 4px 14px rgba(59,130,246,0.4)'
  },
  buttonIcon: {
    fontSize: '1.3rem'
  },
  buttonHint: {
    textAlign: 'center',
    fontSize: '0.8rem',
    color: '#94a3b8',
    marginTop: '8px'
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    margin: '24px 0',
    gap: '16px'
  },
  dividerText: {
    color: '#94a3b8',
    fontSize: '0.8rem',
    fontWeight: '600',
    background: 'white',
    padding: '0 12px'
  },
  inputGroup: {
    display: 'flex',
    gap: '12px'
  },
  input: {
    flex: 1,
    padding: '16px 18px',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    fontSize: '1rem',
    fontFamily: 'monospace',
    letterSpacing: '2px',
    outline: 'none',
    transition: 'border-color 0.2s',
    textAlign: 'center',
    textTransform: 'uppercase'
  },
  joinButton: {
    padding: '16px 28px',
    background: 'linear-gradient(135deg, #10b981, #059669)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(16,185,129,0.3)'
  },
  featuresList: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '14px',
    marginTop: '24px',
    padding: '20px',
    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
    borderRadius: '16px',
    border: '1px solid #e2e8f0'
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '0.85rem',
    color: '#475569',
    fontWeight: '500'
  },
  featureIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    background: 'white',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
  },
  footer: {
    textAlign: 'center',
    paddingTop: '16px',
    borderTop: '1px solid #e2e8f0'
  },
  footerText: {
    color: '#94a3b8',
    fontSize: '0.8rem',
    margin: 0
  },
  
  // Waiting Room Styles
  waitingRoomCard: {
    background: 'white',
    borderRadius: '24px',
    padding: '48px 40px',
    maxWidth: '420px',
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 25px 60px rgba(0,0,0,0.3)'
  },
  waitingAnimation: {
    position: 'relative',
    width: '120px',
    height: '120px',
    margin: '0 auto 24px'
  },
  pulseCircle: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    background: 'rgba(59,130,246,0.2)',
    animation: 'pulse 2s infinite'
  },
  waitingIcon: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    fontSize: '3rem'
  },
  waitingTitle: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#0f172a',
    marginBottom: '8px'
  },
  waitingSubtitle: {
    color: '#64748b',
    marginBottom: '16px'
  },
  waitingInfo: {
    background: '#f1f5f9',
    padding: '12px 20px',
    borderRadius: '10px',
    marginBottom: '24px',
    color: '#475569'
  },
  waitingPreview: {
    position: 'relative',
    borderRadius: '16px',
    overflow: 'hidden',
    marginBottom: '24px'
  },
  waitingVideo: {
    width: '100%',
    height: '200px',
    objectFit: 'cover',
    background: '#1e293b'
  },
  previewControls: {
    position: 'absolute',
    bottom: '12px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: '8px'
  },
  previewButton: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s'
  },
  cancelButton: {
    padding: '14px 32px',
    background: '#f1f5f9',
    color: '#64748b',
    border: 'none',
    borderRadius: '10px',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer'
  },

  // Call View Styles
  callContainer: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: '#0a0a14',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 9999
  },
  callHeader: {
    padding: '12px 20px',
    background: 'rgba(255,255,255,0.03)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    flexWrap: 'wrap',
    gap: '10px'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap'
  },
  roomBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 14px',
    background: 'rgba(255,255,255,0.08)',
    borderRadius: '10px'
  },
  liveDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: '#22c55e',
    animation: 'pulse 2s infinite'
  },
  roomIdText: {
    color: 'white',
    fontFamily: 'monospace',
    fontSize: '0.9rem',
    fontWeight: '600',
    letterSpacing: '1px'
  },
  durationBadge: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: '0.85rem',
    background: 'rgba(255,255,255,0.06)',
    padding: '6px 12px',
    borderRadius: '8px',
    fontFamily: 'monospace'
  },
  hostBadge: {
    background: 'linear-gradient(135deg, rgba(234,179,8,0.25), rgba(251,191,36,0.25))',
    color: '#fbbf24',
    padding: '6px 12px',
    borderRadius: '8px',
    fontSize: '0.75rem',
    fontWeight: '600',
    border: '1px solid rgba(234,179,8,0.3)'
  },
  headerCenter: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  hdIndicator: {
    background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
    color: 'white',
    padding: '5px 12px',
    borderRadius: '8px',
    fontSize: '0.7rem',
    fontWeight: '700',
    letterSpacing: '0.5px'
  },
  participantBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: 'rgba(255,255,255,0.8)',
    fontSize: '0.85rem',
    background: 'rgba(255,255,255,0.06)',
    padding: '6px 12px',
    borderRadius: '8px'
  },
  headerRight: {
    display: 'flex',
    gap: '8px'
  },
  headerButton: {
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.1)',
    color: 'white',
    padding: '10px 12px',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  headerBtnActive: {
    background: 'rgba(59,130,246,0.25)',
    borderColor: 'rgba(59,130,246,0.4)'
  },
  chatBadge: {
    position: 'absolute',
    top: '-4px',
    right: '-4px',
    background: '#ef4444',
    color: 'white',
    fontSize: '0.65rem',
    padding: '2px 6px',
    borderRadius: '10px',
    fontWeight: '600'
  },

  // Waiting Notification
  waitingNotification: {
    background: 'linear-gradient(135deg, rgba(59,130,246,0.15), rgba(139,92,246,0.15))',
    padding: '14px 24px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '12px',
    color: 'white'
  },
  waitingNotificationText: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontWeight: '500'
  },
  waitingActions: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap'
  },
  waitingGuest: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    background: 'rgba(0,0,0,0.25)',
    padding: '10px 14px',
    borderRadius: '10px',
    fontSize: '0.9rem'
  },
  admitBtn: {
    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
    color: 'white',
    border: 'none',
    padding: '8px 14px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.8rem',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    transition: 'all 0.2s'
  },
  declineBtn: {
    background: 'rgba(239,68,68,0.2)',
    color: '#ef4444',
    border: '1px solid rgba(239,68,68,0.3)',
    padding: '8px 10px',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s'
  },

  // Main Content
  mainContent: {
    flex: 1,
    display: 'flex',
    overflow: 'hidden',
    position: 'relative'
  },
  videoGrid: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: '16px',
    gap: '16px',
    overflow: 'hidden',
    transition: 'margin-right 0.3s'
  },
  screenShareMain: {
    flex: 1,
    position: 'relative',
    borderRadius: '16px',
    overflow: 'hidden',
    background: '#111827',
    minHeight: '300px'
  },
  screenShareVideo: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    background: '#000'
  },
  screenShareLabel: {
    position: 'absolute',
    top: '12px',
    left: '12px',
    background: 'rgba(34,197,94,0.9)',
    color: 'white',
    padding: '8px 14px',
    borderRadius: '8px',
    fontSize: '0.85rem',
    fontWeight: '600'
  },
  videoTiles: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '12px',
    flex: 1,
    alignContent: 'center'
  },
  videoTilesWithScreen: {
    maxHeight: '200px',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 200px))',
    justifyContent: 'center'
  },
  videoTile: {
    position: 'relative',
    borderRadius: '16px',
    overflow: 'hidden',
    background: '#1e293b',
    aspectRatio: '16/9',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s'
  },
  pinnedTile: {
    gridColumn: '1 / -1',
    maxWidth: '800px',
    margin: '0 auto',
    boxShadow: '0 0 0 3px #3b82f6'
  },
  tileVideo: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  noVideoOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
  },
  tileOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: '40px 12px 12px',
    background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end'
  },
  tileName: {
    color: 'white',
    fontSize: '0.85rem',
    fontWeight: '600',
    background: 'rgba(0,0,0,0.4)',
    padding: '6px 10px',
    borderRadius: '6px'
  },
  tileStatus: {
    display: 'flex',
    gap: '4px'
  },
  statusIcon: {
    background: 'rgba(239,68,68,0.8)',
    padding: '6px 8px',
    borderRadius: '6px',
    fontSize: '0.8rem'
  },
  emptyTile: {
    gridColumn: '1 / -1',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    background: 'rgba(255,255,255,0.02)',
    borderRadius: '16px',
    border: '2px dashed rgba(255,255,255,0.1)'
  },
  emptyIcon: {
    marginBottom: '16px',
    opacity: 0.6
  },
  emptyTitle: {
    color: 'white',
    fontSize: '1.2rem',
    marginBottom: '8px',
    fontWeight: '600'
  },
  emptySubtitle: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: '0.9rem',
    marginBottom: '24px'
  },
  shareOptions: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
    justifyContent: 'center'
  },
  shareBtn: {
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.12)',
    color: 'white',
    padding: '10px 18px',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: '500',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  shareBtnPrimary: {
    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
    border: 'none'
  },

  // Side Panel
  sidePanel: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    width: '320px',
    background: 'rgba(15,23,42,0.98)',
    borderLeft: '1px solid rgba(255,255,255,0.08)',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 10
  },
  panelHeader: {
    padding: '16px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  panelTitle: {
    color: 'white',
    fontSize: '1rem',
    fontWeight: '600',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  panelClose: {
    background: 'rgba(255,255,255,0.1)',
    border: 'none',
    color: 'white',
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.2s'
  },

  // Chat
  chatMessages: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px'
  },
  chatEmpty: {
    color: 'rgba(255,255,255,0.4)',
    textAlign: 'center',
    padding: '40px 20px'
  },
  chatMessage: {
    marginBottom: '16px'
  },
  chatMsgHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '4px'
  },
  chatSender: {
    color: '#3b82f6',
    fontSize: '0.8rem',
    fontWeight: '600'
  },
  chatTime: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: '0.7rem'
  },
  chatText: {
    color: 'white',
    fontSize: '0.9rem',
    background: 'rgba(255,255,255,0.05)',
    padding: '10px 12px',
    borderRadius: '0 10px 10px 10px'
  },
  chatInputArea: {
    padding: '12px 16px',
    borderTop: '1px solid rgba(255,255,255,0.08)',
    display: 'flex',
    gap: '8px'
  },
  chatInput: {
    flex: 1,
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '10px',
    padding: '12px 14px',
    color: 'white',
    fontSize: '0.9rem',
    outline: 'none'
  },
  chatSendBtn: {
    background: '#3b82f6',
    border: 'none',
    color: 'white',
    width: '44px',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '1.1rem'
  },

  // Participants List
  participantsList: {
    flex: 1,
    overflowY: 'auto',
    padding: '12px'
  },
  participantItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    background: 'rgba(255,255,255,0.03)',
    borderRadius: '10px',
    marginBottom: '8px'
  },
  participantAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: '700',
    fontSize: '1rem'
  },
  participantInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column'
  },
  participantName: {
    color: 'white',
    fontSize: '0.9rem',
    fontWeight: '500'
  },
  participantRole: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: '0.75rem'
  },
  participantStatus: {
    display: 'flex',
    gap: '4px',
    fontSize: '0.9rem'
  },

  // Control Bar
  controlBar: {
    padding: '16px 24px',
    background: 'linear-gradient(180deg, rgba(15,23,42,0.95) 0%, rgba(15,23,42,0.98) 100%)',
    backdropFilter: 'blur(20px)',
    borderTop: '1px solid rgba(255,255,255,0.08)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '24px',
    flexWrap: 'wrap'
  },
  controlGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  controlBtnWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px'
  },
  controlBtn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
    padding: '14px 22px',
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '16px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    minWidth: '75px',
    color: 'white',
    position: 'relative'
  },
  controlBtnOff: {
    background: 'rgba(239,68,68,0.25)',
    borderColor: 'rgba(239,68,68,0.4)'
  },
  controlBtnActive: {
    background: 'rgba(34,197,94,0.25)',
    borderColor: 'rgba(34,197,94,0.4)'
  },
  controlIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  controlText: {
    color: 'rgba(255,255,255,0.85)',
    fontSize: '0.7rem',
    fontWeight: '500',
    letterSpacing: '0.3px'
  },
  controlBadge: {
    position: 'absolute',
    top: '6px',
    right: '6px',
    background: '#ef4444',
    color: 'white',
    fontSize: '0.6rem',
    padding: '2px 5px',
    borderRadius: '8px',
    fontWeight: '600',
    minWidth: '16px',
    textAlign: 'center'
  },
  audioLevelBar: {
    width: '60px',
    height: '4px',
    background: 'rgba(255,255,255,0.15)',
    borderRadius: '2px',
    overflow: 'hidden',
    marginTop: '4px'
  },
  audioLevel: {
    height: '100%',
    borderRadius: '2px',
    transition: 'width 0.1s ease'
  },
  leaveBtn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
    padding: '14px 28px',
    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    border: 'none',
    borderRadius: '16px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    minWidth: '85px',
    color: 'white',
    boxShadow: '0 4px 15px rgba(239,68,68,0.35)'
  },
  
  // Device Settings Dropdown
  deviceDropdown: {
    position: 'absolute',
    bottom: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    marginBottom: '12px',
    background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
    borderRadius: '16px',
    padding: '0',
    minWidth: '280px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
    border: '1px solid rgba(255,255,255,0.1)',
    overflow: 'hidden',
    zIndex: 100
  },
  deviceDropdownHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '16px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    color: 'white',
    fontWeight: '600',
    fontSize: '0.95rem'
  },
  deviceDropdownClose: {
    marginLeft: 'auto',
    background: 'rgba(255,255,255,0.1)',
    border: 'none',
    color: 'white',
    width: '28px',
    height: '28px',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  deviceSection: {
    padding: '16px 20px'
  },
  deviceLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: 'rgba(255,255,255,0.7)',
    fontSize: '0.8rem',
    fontWeight: '500',
    marginBottom: '10px'
  },
  deviceSelect: {
    width: '100%',
    padding: '12px 14px',
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '10px',
    color: 'white',
    fontSize: '0.85rem',
    cursor: 'pointer',
    outline: 'none',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23ffffff' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center'
  },
  micLevelContainer: {
    marginTop: '10px',
    height: '6px',
    background: 'rgba(255,255,255,0.1)',
    borderRadius: '3px',
    overflow: 'hidden'
  },
  micLevelBar: {
    height: '100%',
    background: 'linear-gradient(90deg, #22c55e 0%, #4ade80 100%)',
    borderRadius: '3px',
    transition: 'width 0.1s ease'
  }
};

// Add CSS animation for pulse and spin effects
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = `
    @keyframes pulse {
      0% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
      50% { transform: translate(-50%, -50%) scale(1.5); opacity: 0.5; }
      100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
    }
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    .video-conference select option {
      background: #1e293b;
      color: white;
    }
  `;
  if (!document.querySelector('style[data-video-conference]')) {
    styleSheet.setAttribute('data-video-conference', 'true');
    document.head.appendChild(styleSheet);
  }
}

export default VideoConference;
